import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getHotRanking, searchMovies } from '@/lib/movies';

const hotKeywords = ['都市', '夜色霓虹', '京都雨停之后', '冬季', '海边', '文艺'];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = (searchParams.get('q') ?? '').trim();
  const [keyword, setKeyword] = useState(query);
  const results = query ? searchMovies(query) : [];

  usePageMeta({
    title: query ? `搜索「${query}」 - av免费网站` : '搜索影片 - av免费网站',
    description: query
      ? `在 av免费网站 搜索「${query}」，共找到 ${results.length} 部相关影片（虚构示例内容），可继续筛选或查看详情。`
      : '输入影片标题、演员或标签关键词，快速查找 av免费网站 中的高清影片资料（演示内容）。',
    keywords: 'av免费网站,影片搜索,AV在线观看,高清影视',
  });

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const submit = (value: string) => {
    const next = value.trim();
    if (!next) return;
    navigate(`/search?q=${encodeURIComponent(next)}`);
  };

  const fallback = getHotRanking(4);

  return (
    <div>
      <section className="border-b border-background-200/70 bg-background-100">
        <div className="max-w-page mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-8 md:pb-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
            <span className="text-foreground-800">搜索</span>
          </nav>

          <div className="mt-6 max-w-3xl">
            <p className="text-[11px] tracking-[0.3em] text-secondary-600 font-label uppercase">Search</p>
            <h1 className="mt-2 text-2xl md:text-4xl font-black tracking-tight">搜索影片</h1>
            <p className="mt-3 text-sm text-foreground-600">
              支持标题、外文名、演员、地区与标签匹配。搜索为前端模拟演示，不请求任何外部接口。
            </p>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                submit(keyword);
              }}
              className="mt-6 flex flex-col sm:flex-row gap-3"
            >
              <div className="relative flex-1 min-w-0">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-lg w-5 h-5 flex items-center justify-center" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="例如：夜色霓虹 / 水野纱希 / 都市"
                  className="w-full h-12 md:h-14 pl-12 pr-4 rounded-md bg-background-50 border border-background-300/70 text-sm md:text-base text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-500/70 focus:ring-2 focus:ring-primary-500/20 transition"
                  aria-label="搜索关键词"
                />
              </div>
              <button
                type="submit"
                className="h-12 md:h-14 px-7 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">热门搜索</span>
              {hotKeywords.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => submit(item)}
                  className="h-8 px-3 rounded-full bg-background-200/70 border border-background-300/50 text-xs text-foreground-700 hover:text-primary-400 hover:border-primary-500/50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-page mx-auto px-4 md:px-8 py-8 md:py-12">
        {query && results.length > 0 && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-base md:text-lg font-bold">
                搜索「<span className="text-primary-400">{query}</span>」的结果
              </h2>
              <p className="text-xs text-foreground-500">
                共找到 <span className="text-foreground-900">{results.length}</span> 部相关影片
              </p>
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {results.map((movie, index) => (
                <div key={movie.id} style={{ animationDelay: `${index * 40}ms` }}>
                  <MovieCard movie={movie} />
                </div>
              ))}
            </div>
          </>
        )}

        {query && results.length === 0 && (
          <div className="rounded-lg border border-background-300/60 bg-background-100 py-14 md:py-20 px-6 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-search-eye-line text-3xl" />
            </span>
            <h2 className="mt-5 text-lg md:text-xl font-bold">没有找到「{query}」相关的影片</h2>
            <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto leading-relaxed">
              试试更短的关键词，或者换一个演员、地区、标签搜索。也可以直接从热榜里挑一部开始。
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="h-11 px-6 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium flex items-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to="/category/latest"
                className="h-11 px-6 rounded-md border border-background-300/70 text-sm text-foreground-800 flex items-center hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览最新影片
              </Link>
            </div>

            <div className="mt-10 text-left">
              <p className="text-xs text-foreground-500">也许你会喜欢</p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {fallback.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showSynopsis={false} />
                ))}
              </div>
            </div>
          </div>
        )}

        {!query && (
          <div className="rounded-lg border border-background-300/60 bg-background-100 p-5 md:p-8">
            <h2 className="text-lg md:text-xl font-bold">先从这些开始</h2>
            <p className="mt-2 text-sm text-foreground-600">
              还没有输入关键词。下面是本周播放量最高的几部影片，也可以直接进入分类页慢慢挑。
            </p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {fallback.map((movie) => (
                <MovieCard key={movie.id} movie={movie} showSynopsis={false} />
              ))}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/category/latest"
                className="h-10 px-4 rounded-md border border-background-300/70 text-sm text-foreground-800 flex items-center hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                最新影片
              </Link>
              <Link
                to="/category/hot"
                className="h-10 px-4 rounded-md border border-background-300/70 text-sm text-foreground-800 flex items-center hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                热门影片
              </Link>
              <Link
                to="/category/classic"
                className="h-10 px-4 rounded-md border border-background-300/70 text-sm text-foreground-800 flex items-center hover:border-primary-500/60 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                经典影片
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}